import cx from "classnames";
import { faAngleRight, faPaw } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import { toLocaleDate } from "common/utils/dates";

import Box from "client/common/components/layouts/Box";
import TextChip, { TextChipProps } from "client/common/components/content/TextChip";

import { DosageReminder, DosageReminderStatus } from "../types";
import { SPECIES_ICONS } from "../constants";
import { useReminderDrawer } from "../stackables";
import { getFrequencyLabel } from "../utils";

import styles from "../styles/ReminderCard.module.less";

const toMonthDay = (date: string) => toLocaleDate(date, { month: "short", day: "numeric" });

type ReminderCardProps = {
  reminder: DosageReminder;
  patientChipVariant: TextChipProps["variant"];
};

const ReminderCard = ({ reminder, patientChipVariant }: ReminderCardProps) => {
  const reminderDrawer = useReminderDrawer();
  const productName = reminder.item.product.product_line.name;

  return (
    <LinkButton dialog={reminderDrawer({ reminder })}>
      <Box className={cx(styles.card, !reminder.is_active && styles.inactive)}>
        <CatalogImage
          image={reminder.item.image}
          size="thumbnail"
          alt={productName}
          className={styles.image}
        />
        <div className={styles.content}>
          <span className={styles.name}>{productName}</span>
          <TextChip variant={patientChipVariant}>
            <Icon
              icon={SPECIES_ICONS[reminder.patient.species] ?? faPaw}
              className={styles.patientIcon}
            />
            <span className={styles.patientName}>{reminder.patient.name}</span>
          </TextChip>
        </div>
        <div className={styles.schedule}>
          {reminder.is_active ? (
            <>
              {reminder.next_dose_date && (
                <span className={styles.nextDose}>Next {toMonthDay(reminder.next_dose_date)}</span>
              )}
              <span className={styles.frequency}>{getFrequencyLabel(reminder)}</span>
              {reminder.stop_date && (
                <span className={styles.stopDate}>Until {toMonthDay(reminder.stop_date)}</span>
              )}
            </>
          ) : (
            <>
              {reminder.inactive_date && (
                <span className={styles.stoppedDate}>
                  {reminder.status === DosageReminderStatus.Paused ? "Paused" : "Stopped"}{" "}
                  {toMonthDay(reminder.inactive_date)}
                </span>
              )}
              {reminder.last_sent_date && (
                <span className={styles.lastSentDate}>
                  Last on{" "}
                  {toLocaleDate(reminder.last_sent_date, { month: "short", year: "numeric" })}
                </span>
              )}
            </>
          )}
        </div>
        <Icon icon={faAngleRight} className={styles.chevron} />
      </Box>
    </LinkButton>
  );
};

export default ReminderCard;
